/**
 * Core Teen Patti Game Types (Indian Three-Card Poker)
 *
 * All TypeScript types, interfaces, enums, and constants
 * for the Teen Patti game implementation.
 */

// ============================================================================
// Card Types
// ============================================================================

export type Suit = 'hearts' | 'diamonds' | 'clubs' | 'spades';
export type Rank = '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A';

export interface Card {
  suit: Suit;
  rank: Rank;
}

export const RANK_VALUES: Record<Rank, number> = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7,
  '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14,
};

export const SUIT_VALUES: Record<Suit, number> = {
  'spades': 4, 'hearts': 3, 'diamonds': 2, 'clubs': 1,
};

export const SUITS: Suit[] = ['hearts', 'diamonds', 'clubs', 'spades'];
export const RANKS: Rank[] = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

// ============================================================================
// Deck Types
// ============================================================================

export interface Deck {
  cards: Card[];
  dealtCards: Card[];
}

// ============================================================================
// Hand Evaluation Types
// ============================================================================

export enum TeenPattiHandRank {
  HIGH_CARD = 0,
  PAIR = 1,
  COLOR = 2,
  SEQUENCE = 3,
  PURE_SEQUENCE = 4,
  TRAIL = 5,
}

export interface TeenPattiHandResult {
  rank: TeenPattiHandRank;
  rankName: string;
  cards: Card[];
  comparisonKey: number[];
}

// ============================================================================
// Player Types
// ============================================================================

export type TeenPattiPlayerStatus = 'active' | 'packed' | 'eliminated' | 'sitting-out';
export type TeenPattiDifficulty = 'easy' | 'medium' | 'hard';
export type BlindSeenStatus = 'blind' | 'seen';

export interface TeenPattiPlayerState {
  id: string;
  name: string;
  seatIndex: number;
  chipStack: number;
  cards: Card[];
  status: TeenPattiPlayerStatus;
  blindSeenStatus: BlindSeenStatus;
  currentBetThisRound: number;
  isDealer: boolean;
  isHuman: boolean;
  aiDifficulty?: TeenPattiDifficulty;
  hasActedThisRound: boolean;
}

export interface VisibleTeenPattiPlayerState {
  id: string;
  name: string;
  seatIndex: number;
  chipStack: number;
  cards: Card[] | null;
  status: TeenPattiPlayerStatus;
  blindSeenStatus: BlindSeenStatus;
  currentBetThisRound: number;
  isDealer: boolean;
  hasActedThisRound: boolean;
}

// ============================================================================
// Betting Action Types
// ============================================================================

export type TeenPattiActionType = 'pack' | 'call' | 'raise' | 'see-cards' | 'sideshow' | 'show';

export interface TeenPattiAction {
  type: TeenPattiActionType;
  amount?: number;
}

export interface ValidTeenPattiAction {
  type: TeenPattiActionType;
  minAmount?: number;
  maxAmount?: number;
  callAmount?: number;
}

export interface ActionResult {
  success: boolean;
  action?: TeenPattiActionRecord;
  error?: string;
  roundComplete?: boolean;
  showdown?: ShowdownResult;
  sideshowPending?: boolean;
}

export interface TeenPattiActionRecord {
  playerId: string;
  playerName: string;
  action: TeenPattiActionType;
  amount: number;
  wasBlind: boolean;
  timestamp: number;
}

// ============================================================================
// Sideshow Types
// ============================================================================

export interface SideshowRequest {
  requesterId: string;
  targetId: string;
  pending: boolean;
}

export interface SideshowResult {
  requesterId: string;
  targetId: string;
  accepted: boolean;
  loserId?: string;
}

// ============================================================================
// Showdown Types
// ============================================================================

export interface ShowdownResult {
  playerResults: ShowdownPlayerResult[];
  winnerId: string;
  potAmount: number;
  isForcedShowdown: boolean;
}

export interface ShowdownPlayerResult {
  playerId: string;
  playerName: string;
  cards: Card[];
  handResult: TeenPattiHandResult;
}

// ============================================================================
// Game State Types
// ============================================================================

export type TeenPattiGameStatus =
  | { type: 'waiting' }
  | { type: 'active' }
  | { type: 'roundInProgress' }
  | { type: 'betweenRounds' }
  | { type: 'sideshowPending'; requesterId: string; targetId: string }
  | { type: 'complete'; winnerId: string };

export interface TeenPattiGameState {
  tableSize: number;
  buyIn: number;
  bootAmount: number;
  potLimit: number | null;
  players: TeenPattiPlayerState[];
  dealerSeatIndex: number;
  roundNumber: number;
  deck: Deck;
  pot: number;
  currentStake: number;
  currentPlayerIndex: number;
  actionHistory: TeenPattiActionRecord[];
  pendingSideshow: SideshowRequest | null;
  gameStatus: TeenPattiGameStatus;
  roundHistory: RoundSummary[];
}

export interface RoundSummary {
  roundNumber: number;
  winnerId: string;
  winnerName: string;
  winningHand?: TeenPattiHandResult;
  potAmount: number;
  playerCount: number;
  endedBy: 'pack' | 'showdown' | 'pot-limit' | 'sideshow';
}

// ============================================================================
// Visible Game State (Client-Side)
// ============================================================================

export interface VisibleTeenPattiState {
  tableSize: number;
  buyIn: number;
  bootAmount: number;
  potLimit: number | null;
  players: VisibleTeenPattiPlayerState[];
  dealerSeatIndex: number;
  roundNumber: number;
  pot: number;
  currentStake: number;
  currentPlayerIndex: number;
  actionHistory: TeenPattiActionRecord[];
  pendingSideshow: SideshowRequest | null;
  gameStatus: TeenPattiGameStatus;
}

// ============================================================================
// Game Configuration Types
// ============================================================================

export interface TeenPattiGameConfig {
  tableSize: number;
  buyIn: number;
  bootAmount: number;
  potLimit: number | null;
  players: TeenPattiPlayerConfig[];
}

export interface TeenPattiPlayerConfig {
  id: string;
  name: string;
  isHuman: boolean;
  aiDifficulty?: TeenPattiDifficulty;
}

// ============================================================================
// Game Result and Statistics Types
// ============================================================================

export interface TeenPattiGameResult {
  gameId: string;
  mode: 'singleplayer' | 'multiplayer-realtime';
  difficulty?: TeenPattiDifficulty;
  finalPosition: number;
  roundsPlayed: number;
  roundsWon: number;
  totalChipsWon: number;
  totalChipsLost: number;
  biggestPotWon: number;
  duration: number;
  completedAt: number;
}

export interface TeenPattiStatistics {
  userId?: string;
  singlePlayer: {
    easy: TeenPattiGameStats;
    medium: TeenPattiGameStats;
    hard: TeenPattiGameStats;
  };
  onlineMultiplayer: TeenPattiGameStats;
  totalGames: number;
  totalPlayTime: number;
}

export interface TeenPattiGameStats {
  gamesPlayed: number;
  gamesWon: number;
  winPercentage: number;
  roundsPlayed: number;
  roundsWon: number;
  totalChipsWon: number;
  totalChipsLost: number;
  biggestPotWon: number;
}

// ============================================================================
// Game Settings Types
// ============================================================================

export interface TeenPattiGameSettings {
  mode: 'singleplayer' | 'multiplayer-realtime';
  aiDifficulty?: TeenPattiDifficulty;
  aiOpponentCount?: number;
  tableSize?: number;
  bootAmount?: number;
  potLimit?: number | null;
  buyIn?: number;
  isPrivate?: boolean;
}

// ============================================================================
// Component Props Types
// ============================================================================

export interface TeenPattiTableProps {
  gameState: VisibleTeenPattiState;
  currentPlayerId: string;
  onAction: (action: TeenPattiAction) => void;
  isMultiplayer: boolean;
  turnTimeRemaining?: number;
}

export interface TeenPattiBettingControlsProps {
  validActions: ValidTeenPattiAction[];
  pot: number;
  currentStake: number;
  minBet: number;
  maxBet: number;
  playerStack: number;
  isBlind: boolean;
  onAction: (action: TeenPattiAction) => void;
  disabled: boolean;
}

export interface TeenPattiCardsProps {
  cards: Card[] | null;
  size?: 'small' | 'medium' | 'large';
  faceDown?: boolean;
  animateReveal?: boolean;
  highlighted?: boolean;
  isBlind?: boolean;
}

export interface TeenPattiHandRankingProps {
  handResult?: TeenPattiHandResult;
  showRankingGuide?: boolean;
}

export interface TeenPattiGameModeSelectorProps {
  onStartGame: (settings: TeenPattiGameSettings) => void;
  onJoinPrivateGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

export interface TeenPattiGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

// ============================================================================
// Constants
// ============================================================================

export const DEFAULT_BOOT_AMOUNT = 10;
export const DEFAULT_BUY_IN = 1000;
